import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useStickToBottomContext } from 'use-stick-to-bottom'
import {
  Check, X, FileImage, FilePen, FilePlus, FileText, Globe, ListTodo, Loader2, MessageCircleQuestion, Search, Sparkles,
  Square, SquareTerminal, Wrench, Copy, RotateCcw,
} from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import type { BundledLanguage } from 'shiki'
import type { ChatItem, ChatQuestion } from '@one-deployment/protocol'
import { Conversation, ConversationContent, ConversationScrollButton } from '@/components/ai-elements/conversation'
import { Message, MessageContent, MessageResponse } from '@/components/ai-elements/message'
import { Reasoning, ReasoningContent, ReasoningTrigger } from '@/components/ai-elements/reasoning'
import { CodeBlock, CodeBlockCopyButton } from '@/components/ai-elements/code-block'
import { Shimmer } from '@/components/ai-elements/shimmer'
import { cn } from '@/lib/cn'
import { findLinks, shortLink } from './links'
import { splitUploads } from './uploads'
import { FilePreview, formatBytes } from './AttachmentTray'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/shadcn/hover-card'
import { downloadOnce } from '@/lib/files'
import { useUploadNames } from './UploadsPanel'
import { LimitNotice, isLimitText } from './LimitNotice'

/**
 * The conversation, drawn with AI Elements (shadcn's chat components):
 * messages, reasoning, tool calls as cards with highlighted commands, diffs
 * and output. It uses the pane's full width; only the composer is narrow.
 */
/** What you can do to a message of yours. */
export type MessageActions = {
  /** Rewind the conversation to before this message; absent while Claude works or read-only. */
  rewind?: (id: string) => void
  /** A held message: drop it, or type it in now instead of after the turn. */
  cancel?: (id: string) => void
  sendNow?: (id: string) => void
  /** Stream mode's rewind: needs the message's own uuid and the line before it. */
  rewindStream?: (b: { id: string; prev?: string | null }) => void
}

export function Transcript({ items, working, loading, sent = [], held = [], actions = {}, live }: {
  items: ChatItem[]; working: boolean; loading: boolean
  /** Typed into the session but not yet in its transcript: Claude Code reads it after the current step. */
  sent?: { id: string; text: string }[]
  /** Written while Claude works and kept here; it goes in when the turn ends. */
  held?: { id: string; text: string }[]
  actions?: MessageActions
  /** The live status lines (ONE-9), under the working line. */
  live?: ReactNode
}) {
  const results = useMemo(() => new Map(items.filter((i) => i.kind === 'tool_result').map((i) => [i.tool_use_id!, i])), [items])
  // What was already there when the conversation loaded shows at once; only
  // replies that arrive while watching are typed out.
  // Captured at the first items, not when the socket opens: the history
  // arrives just after, and must not be typed out as if it were new.
  const history = useRef<Set<string> | null>(null)
  if (history.current === null && items.length) history.current = new Set(items.map((i) => i.id))
  // The history lands in pieces and its code and tables lay out late; keep it
  // hidden until it has settled at the bottom, so opening a session shows the
  // end at once instead of a scroll down through everything.
  const [settled, setSettled] = useState(false)
  const any = items.length > 0
  useEffect(() => {
    if (!any || settled) return
    const t = setTimeout(() => setSettled(true), 250)
    return () => clearTimeout(t)
  }, [any, settled, items.length])
  return (
    // Instant, not smooth: a session opens already at its end, with no long scroll down.
    <Conversation className="min-h-0" initial="instant" resize="instant">
      <ConversationContent className={cn('gap-4 px-6 py-5', any && !settled && 'invisible')}>
        {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin" />Loading the conversation…</div>}
        {!loading && !items.length && <div className="py-10 text-center text-sm text-fg-faint">Nothing said yet.</div>}
        {blocks(items).map((b) => {
          if (Array.isArray(b)) return <ToolGrid key={b[0]!.id} calls={b} results={results} />
          const it = b
          switch (it.kind) {
            case 'user': if (/^\[Request interrupted/.test(it.text ?? '')) return <div key={it.id} className="text-xs text-fg-faint">Stopped</div>
              return <UserMessage key={it.id} text={it.text ?? ''} rewind={actions.rewind && (() => actions.rewind!(it.id))} />
            case 'assistant': if (isLimitText(it.text ?? '')) return <LimitNotice key={it.id} text={it.text ?? ''} />
              return <Message key={it.id} from="assistant" className="max-w-none"><MessageContent className="w-full"><Typed text={it.text ?? ''} animate={!history.current?.has(it.id)} /></MessageContent></Message>
            case 'thinking': return <Thinking key={it.id} text={it.text ?? ''} />
            case 'tool_use': return <Special key={it.id} call={it} result={results.get(it.tool_use_id ?? '')} />
            default: return null
          }
        })}
        {/* What you sent sits where it will land, above the working line, not below it. */}
        {sent.map((q) => <UserMessage key={q.id} text={q.text} />)}
        {working && <Working items={items} results={results} />}
        {live}
        {held.map((q) => <UserMessage key={q.id} text={q.text} held cancel={() => actions.cancel?.(q.id)} sendNow={() => actions.sendNow?.(q.id)} />)}
      </ConversationContent>
      <ConversationScrollButton className="bottom-3 border bg-surface-3" />
    </Conversation>
  )
}

const dur = (ms: number) => { const t = Math.max(0, Math.round(ms / 1000)); return t < 60 ? `${t}s` : `${Math.floor(t / 60)}m ${t % 60}s` }
/** No new output for this long and the line says so, with what to do. */
const QUIET_MS = 90_000

/**
 * What Claude is doing while it works, not just that it is: the tool it is
 * running or that it is thinking, for how long, and when it last showed
 * anything. A long silence is named, so a hang does not look like progress.
 */
/** Characters per frame: a reply of any length is typed out within about a second. */
const typeStep = (len: number) => Math.max(4, Math.ceil(len / 60))

/**
 * An assistant reply, revealed a few characters a frame the way Claude Code
 * prints. Claude writes its transcript one whole message at a time, so the
 * box never has the tokens; this only paces what has arrived.
 */
function Typed({ text, animate }: { text: string; animate: boolean }) {
  const [n, setN] = useState(animate ? 0 : text.length)
  useEffect(() => {
    if (n >= text.length) return
    const f = requestAnimationFrame(() => setN((v) => Math.min(text.length, v + typeStep(text.length))))
    return () => cancelAnimationFrame(f)
  }, [n, text.length])
  // Tables sit in the text like the rest of it: no card, no copy/download/expand.
  return <MessageResponse controls={{ table: false }} className="leading-relaxed [&_[data-streamdown=table-wrapper]]:my-3 [&_[data-streamdown=table-wrapper]]:rounded-none [&_[data-streamdown=table-wrapper]]:border-0 [&_[data-streamdown=table-wrapper]]:bg-transparent [&_[data-streamdown=table-wrapper]]:p-0">{text.slice(0, n)}</MessageResponse>
}

function Working({ items, results }: { items: ChatItem[]; results: Map<string, ChatItem> }) {
  const [now, setNow] = useState(Date.now())
  // Items can come without a time; then count from when this line appeared.
  const [shown] = useState(Date.now())
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t) }, [])
  const asked = [...items].reverse().find((i) => i.kind === 'user')
  const last = items[items.length - 1]
  const running = [...items].reverse().find((i) => i.kind === 'tool_use' && !results.has(i.tool_use_id ?? ''))
  const what = running
    ? `Running ${describe(running.tool ?? 'tool', (running.input ?? {}) as Record<string, unknown>).title}`
    : last?.kind === 'thinking' ? 'Thinking'
    : last?.kind === 'tool_result' ? 'Reading the result'
    : last?.kind === 'user' ? 'Starting on your message'
    : 'Working out the next step'
  const since = (running ?? last)?.ts || shown
  const quiet = now - (last?.ts || shown)
  return (
    <div className="flex flex-col gap-1 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        <Sparkles size={14} className="shrink-0 animate-pulse text-accent" />
        <Shimmer duration={1.5}>{what}</Shimmer>
        <span className="shrink-0 tabular-nums text-xs text-fg-faint">
          {dur(now - (asked?.ts || since))}
        </span>
      </div>
      {quiet > QUIET_MS && (
        <div className="pl-[22px] text-xs text-warn">
          Nothing new for {dur(quiet)}. {running ? 'The tool may be slow or waiting on a network call.' : 'Claude may be thinking at length, or stuck.'} Press Esc or Stop to interrupt it.
        </div>
      )}
    </div>
  )
}

function Act({ label, hint, onClick, disabled, children }: { label: string; hint?: string; onClick?: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label={label} disabled={disabled} onClick={onClick}
          className="flex size-6 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-hover hover:text-foreground disabled:pointer-events-none disabled:opacity-40">{children}</button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-56"><div className="font-medium">{label}</div>{hint && <div className="text-muted-foreground">{hint}</div>}</TooltipContent>
    </Tooltip>
  )
}

export function UserMessage({ text, note, held = false, rewind, cancel, sendNow }: {
  text: string; note?: string; held?: boolean
  rewind?: () => void; cancel?: () => void; sendNow?: () => void
}) {
  const { files, rest } = splitUploads(text)
  const [copied, setCopied] = useState(false)
  const copy = () => { void navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200) }) }
  return (
    <Message from="user" className={cn('group/msg max-w-[80%]', (held || note) && 'opacity-70')}>
      {files.length > 0 && (
        <div className="flex flex-wrap justify-end gap-1" aria-label="Attached files">
          {files.map((f, i) => <UploadChip key={i} path={f} />)}
        </div>
      )}
      {rest && <MessageContent className="whitespace-pre-wrap break-words rounded-lg border border-border-strong bg-surface-3 px-4 py-2.5 leading-relaxed text-fg group-[.is-user]:bg-surface-3 group-[.is-user]:px-4 group-[.is-user]:py-2.5"><Linked text={rest} /></MessageContent>}
      {note && <span className="self-end text-[11px] text-fg-faint">{note}</span>}
      {!note && (
        <div className={cn('flex items-center justify-end gap-0.5 transition-opacity', !held && 'opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100')}>
          <Act label={copied ? 'Copied' : 'Copy'} onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />}</Act>
          <Act label="Rewind to here" hint="Removes this message and everything after it" onClick={rewind} disabled={!rewind}><RotateCcw size={14} /></Act>
          {held && <>
            <Act label="Cancel" hint="Never sent" onClick={cancel}><X size={14} /></Act>
            <button type="button" onClick={sendNow} className="rounded-md px-1.5 py-0.5 text-xs text-fg-subtle transition-colors hover:bg-hover hover:text-foreground"
              title="Claude reads it after its current step instead of when the turn ends">Send now</button>
          </>}
        </div>
      )}
    </Message>
  )
}

/** A file sent with a message: a small square with its type, the name on hover. */
function UploadChip({ path }: { path: string }) {
  const session = useContext(SessionCtx)
  const name = path.split('/').pop() ?? path
  const ext = name.includes('.') ? name.split('.').pop()!.slice(0, 4).toUpperCase() : 'FILE'
  const Icon = /\.(png|jpe?g|gif|webp|svg|avif)$/i.test(name) ? FileImage : FileText
  const [file, setFile] = useState<File | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const names = useUploadNames(session)
  // Fetched from the session on first hover only, and kept for the page's life.
  const load = (open: boolean) => {
    if (!open || !session || file || err) return
    const key = `${session}:${path}`
    const p = previews.get(key) ?? downloadOnce(session, path)
    previews.set(key, p)
    p.then((b) => setFile(new File([b], name, { type: b.type || mimeOf(name) })), (e: Error) => { previews.delete(key); setErr(e.message) })
  }
  if (names && !names.has(name)) {
    return (
      <span title={`${name} (deleted)`} className="flex size-9 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed border-border-strong text-fg-faint">
        <Icon size={13} className="opacity-50" />
        <span className="text-[7px] font-medium leading-none tracking-wide">Deleted</span>
      </span>
    )
  }
  return (
    <HoverCard openDelay={250} closeDelay={80} onOpenChange={load}>
      <HoverCardTrigger asChild>
        <span tabIndex={0} aria-label={name} className="flex size-9 cursor-default flex-col items-center justify-center gap-0.5 rounded-md border border-border-strong bg-surface-3 text-fg-subtle hover:border-ring/60">
          <Icon size={13} />
          <span className="text-[7px] font-medium leading-none tracking-wide">{ext}</span>
        </span>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="end" className="w-72 overflow-hidden p-0">
        <div className="flex h-44 items-center justify-center overflow-hidden border-b bg-bg">
          {file ? <FilePreview file={file} /> : err ? <span className="px-3 text-xs text-err">{err}</span> : <Loader2 size={14} className="animate-spin text-muted-foreground" />}
        </div>
        <div className="truncate px-3 py-2 text-xs font-medium">{name}{file ? <span className="font-normal text-muted-foreground"> · {formatBytes(file.size)}</span> : null}</div>
      </HoverCardContent>
    </HoverCard>
  )
}

const previews = new Map<string, Promise<Blob>>()
const mimeOf = (n: string) => /\.png$/i.test(n) ? 'image/png' : /\.jpe?g$/i.test(n) ? 'image/jpeg' : /\.gif$/i.test(n) ? 'image/gif' : /\.webp$/i.test(n) ? 'image/webp' : /\.svg$/i.test(n) ? 'image/svg+xml' : /\.pdf$/i.test(n) ? 'application/pdf' : ''

/** The session the transcript belongs to, for previews fetched from its folder. */
export const SessionCtx = createContext<string | undefined>(undefined)

/** A user's message with its links shortened, full link on hover. */
export function Linked({ text }: { text: string }) {
  const links = findLinks(text)
  if (!links.length) return <>{text}</>
  const out: ReactNode[] = []
  let at = 0
  for (const l of links) {
    out.push(text.slice(at, l.start))
    out.push(<a key={l.start} href={l.url} title={l.url} target="_blank" rel="noopener noreferrer" className="rounded-xs bg-accent/15 px-1 font-medium text-accent-hover hover:underline">{shortLink(l.url)}</a>)
    at = l.end
  }
  out.push(text.slice(at))
  return <>{out}</>
}

export function Thinking({ text }: { text: string }) {
  const { stopScroll } = useStickToBottomContext()
  return (
    <Reasoning defaultOpen={false} onOpenChange={() => stopScroll()} className="mb-0">
      <ReasoningTrigger getThinkingMessage={() => <span>Thinking</span>} />
      <ReasoningContent className="mt-2 border-l-2 pl-3 text-xs">{text}</ReasoningContent>
    </Reasoning>
  )
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v))
const base = (p: string) => p.split('/').filter(Boolean).slice(-2).join('/')

/** A tool call's icon and one line saying what it did, the way Claude's apps show them. */
export function describe(tool: string, input: Record<string, unknown>): { icon: ReactNode; title: string; detail?: string } {
  const s = 14
  switch (tool) {
    case 'Bash': return { icon: <SquareTerminal size={s} />, title: str(input.description) || 'Ran a command', detail: str(input.command) }
    case 'Read': return { icon: <FileText size={s} />, title: 'Read', detail: base(str(input.file_path)) }
    case 'Write': return { icon: <FilePlus size={s} />, title: 'Wrote', detail: base(str(input.file_path)) }
    case 'Edit': case 'MultiEdit': return { icon: <FilePen size={s} />, title: 'Edited', detail: base(str(input.file_path)) }
    case 'NotebookEdit': return { icon: <FilePen size={s} />, title: 'Edited', detail: base(str(input.notebook_path)) }
    case 'Grep': return { icon: <Search size={s} />, title: `Searched for ${str(input.pattern)}`, detail: str(input.path) || undefined }
    case 'Glob': return { icon: <Search size={s} />, title: 'Found files', detail: str(input.pattern) }
    case 'WebFetch': return { icon: <Globe size={s} />, title: 'Fetched', detail: str(input.url) }
    case 'WebSearch': return { icon: <Globe size={s} />, title: 'Searched the web', detail: str(input.query) }
    case 'Task': case 'Agent': return { icon: <Sparkles size={s} />, title: str(input.description) || 'Ran an agent' }
    default: return { icon: <Wrench size={s} />, title: tool.startsWith('mcp__') ? tool.split('__').slice(1).join(' · ') : tool }
  }
}

const EXT_LANG: Record<string, BundledLanguage> = {
  ts: 'ts', tsx: 'tsx', js: 'js', jsx: 'jsx', mjs: 'js', json: 'json', rs: 'rust', py: 'python', go: 'go', rb: 'ruby', md: 'markdown',
  css: 'css', scss: 'scss', html: 'html', sql: 'sql', sh: 'bash', yml: 'yaml', yaml: 'yaml', toml: 'toml', tf: 'hcl', hcl: 'hcl', java: 'java',
  kt: 'kotlin', swift: 'swift', php: 'php', vue: 'vue', svelte: 'svelte', dockerfile: 'docker',
}
const langOf = (path: string): BundledLanguage => EXT_LANG[path.split('.').pop()?.toLowerCase() ?? ''] ?? ('text' as BundledLanguage)

function diffText(edits: { old: string; new: string }[]): string {
  return edits.map((e) => [...(e.old ? e.old.split('\n').map((l) => `- ${l}`) : []), ...e.new.split('\n').map((l) => `+ ${l}`)].join('\n')).join('\n···\n')
}

function Code({ code, lang, label }: { code: string; lang: BundledLanguage; label?: string }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <div className="text-[10px] font-medium text-muted-foreground">{label}</div>}
      <CodeBlock code={code} language={lang} className="max-h-80 overflow-auto rounded-lg bg-bg [&_pre]:bg-bg! [&_pre]:p-3! [&_pre]:text-[11.5px]! [&_code]:text-[11.5px]!">
        <CodeBlockCopyButton className="size-6 opacity-0 transition-opacity group-hover:opacity-100" />
      </CodeBlock>
    </div>
  )
}

/** Tools drawn in full in the conversation rather than as a tile. */
const SPECIAL = new Set(['TodoWrite', 'AskUserQuestion'])

/** The transcript with each run of consecutive tool calls gathered into one grid. Results are drawn by their call. */
function blocks(items: ChatItem[]): (ChatItem | ChatItem[])[] {
  const out: (ChatItem | ChatItem[])[] = []
  for (const it of items) {
    if (it.kind === 'tool_result') continue
    const tile = it.kind === 'tool_use' && !SPECIAL.has(it.tool ?? '')
    const last = out[out.length - 1]
    if (tile && Array.isArray(last)) last.push(it)
    else out.push(tile ? [it] : it)
  }
  return out
}

function Special({ call, result }: { call: ChatItem; result: ChatItem | undefined }) {
  const input = (call.input ?? {}) as Record<string, unknown>
  if (call.tool === 'TodoWrite') return <Todos input={input} />
  return <AskedQuestion input={input} result={result} />
}

type ToolState = 'running' | 'done' | 'error'
const stateOf = (r: ChatItem | undefined): ToolState => (!r ? 'running' : r.is_error ? 'error' : 'done')

/**
 * A run of tool calls as small chips side by side, so twenty reads take a
 * line or two, not twenty rows. A click opens that call's details under the
 * run; a finished call shows only a tick.
 */
function ToolGrid({ calls, results }: { calls: ChatItem[]; results: Map<string, ChatItem> }) {
  const [open, setOpen] = useState<string | null>(null)
  // Opening a chip stops following the end, so the details stay where you clicked.
  const { stopScroll } = useStickToBottomContext()
  const cur = calls.find((c) => c.id === open)
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        {calls.map((c) => (
          <ToolTile key={c.id} call={c} state={stateOf(results.get(c.tool_use_id ?? ''))} open={open === c.id}
            onClick={() => { stopScroll(); setOpen((o) => (o === c.id ? null : c.id)) }} />
        ))}
      </div>
      {cur && <ToolDetails call={cur} result={results.get(cur.tool_use_id ?? '')} onClose={() => setOpen(null)} />}
    </div>
  )
}

const SHORT: Record<string, string> = {
  Bash: 'Command', Read: 'Read', Write: 'Write', Edit: 'Edit', MultiEdit: 'Edit', NotebookEdit: 'Edit',
  Grep: 'Search', Glob: 'Find files', WebFetch: 'Fetch', WebSearch: 'Web search', Task: 'Agent', Agent: 'Agent',
  ToolSearch: 'Tools', Skill: 'Skill', TodoWrite: 'Todos',
}
/** A word or two for a tile: the service for an MCP tool (`mcp__stripe__…` is "Stripe"), else the kind of tool. */
export function shortName(tool: string): string {
  const mcp = /^mcp__([^_]+(?:_[^_]+)*?)__/.exec(tool)?.[1]
  if (mcp) return mcp.replace(/^plugin_[^_]+_/, '').replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  return SHORT[tool] ?? tool.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ').slice(0, 2).join(' ')
}

function ToolTile({ call, state, open, onClick }: { call: ChatItem; state: ToolState; open: boolean; onClick: () => void }) {
  const tool = call.tool ?? 'tool'
  const { icon, title, detail } = describe(tool, (call.input ?? {}) as Record<string, unknown>)
  // The whole title and command only on hover and in the details; the chip stays small.
  return (
    <button type="button" onClick={onClick} aria-expanded={open} title={detail ? `${title}\n${detail}` : title}
      className={cn('flex h-6 max-w-40 items-center gap-1 rounded-md border bg-surface-2 px-1.5 text-[11px] text-fg-muted outline-none transition-colors hover:border-border-strong focus-visible:ring-2 focus-visible:ring-ring/30 [&_svg]:size-3',
        open && 'border-ring bg-accent/10', state === 'error' && 'border-err/40')}>
      <span className={cn('flex shrink-0', state === 'error' ? 'text-err' : state === 'running' ? 'text-accent-hover' : 'text-muted-foreground')}>{icon}</span>
      <span className="truncate">{shortName(tool)}</span>
      {state === 'running' ? <Loader2 className="shrink-0 animate-spin text-accent-hover" />
        : state === 'error' ? <X className="shrink-0 text-err" aria-label="Failed" />
        : <Check className="shrink-0 text-ok" aria-label="Done" />}
    </button>
  )
}

function ToolDetails({ call, result, onClose }: { call: ChatItem; result: ChatItem | undefined; onClose: () => void }) {
  const tool = call.tool ?? 'tool'
  const input = (call.input ?? {}) as Record<string, unknown>
  const { icon, title, detail } = describe(tool, input)
  const err = !!result?.is_error
  return (
    <div className="col-span-full flex flex-col gap-3 rounded-lg border border-border-strong bg-surface-2/40 p-3 animate-in fade-in-0 slide-in-from-top-1">
      <div className="flex min-w-0 items-center gap-2 text-[13px]">
        <span className="text-muted-foreground">{icon}</span>
        <span className="shrink-0 font-medium">{title}</span>
        {detail && <span className="truncate font-mono text-xs text-muted-foreground">{detail}</span>}
        <button type="button" onClick={onClose} aria-label="Close" className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-hover"><X size={14} /></button>
      </div>
      {toolBody(tool, input)}
      {result?.text
        ? err
          ? <div className="flex flex-col gap-1"><div className="text-[10px] font-medium text-err">Error</div><pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-err/30 bg-err/5 p-3 font-mono text-[11.5px] text-err">{result.text}</pre></div>
          : <Code code={result.text} lang={'text' as BundledLanguage} label="Output" />
        : !result && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin" />Running…</div>}
    </div>
  )
}

/** What goes in a tool's open card above its output: the command, or the change as a diff. */
function toolBody(tool: string, input: Record<string, unknown>): ReactNode {
  if (tool === 'Bash') return <Code code={str(input.command)} lang="bash" label="Command" />
  if (tool === 'Edit') return <Code code={diffText([{ old: str(input.old_string), new: str(input.new_string) }])} lang="diff" label={str(input.file_path)} />
  if (tool === 'MultiEdit') return <Code code={diffText((input.edits as { old_string: string; new_string: string }[] ?? []).map((e) => ({ old: e.old_string, new: e.new_string })))} lang="diff" label={str(input.file_path)} />
  if (tool === 'Write') return <Code code={str(input.content)} lang={langOf(str(input.file_path))} label={str(input.file_path)} />
  if (tool === 'Task' || tool === 'Agent') return <div className="whitespace-pre-wrap text-xs text-muted-foreground">{str(input.prompt)}</div>
  if (['Read', 'Grep', 'Glob', 'WebFetch', 'WebSearch'].includes(tool)) return null
  return <Code code={JSON.stringify(input, null, 2)} lang="json" label="Input" />
}

function Todos({ input }: { input: Record<string, unknown> }) {
  const todos = (input.todos as { content: string; status: string; activeForm?: string }[] | undefined) ?? []
  const done = todos.filter((t) => t.status === 'completed').length
  return (
    <div className="rounded-lg border bg-surface-2 px-3 py-2.5 text-[13px]">
      <div className="mb-2 flex items-center gap-2 font-medium"><ListTodo size={14} className="text-muted-foreground" />Plan<span className="ml-auto text-xs font-normal text-muted-foreground">{done} of {todos.length}</span></div>
      <ul className="flex flex-col gap-1.5">
        {todos.map((t, i) => (
          <li key={i} className={cn('flex items-start gap-2', t.status === 'completed' && 'text-fg-faint line-through', t.status === 'in_progress' && 'font-medium text-foreground', t.status === 'pending' && 'text-muted-foreground')}>
            <span className="mt-0.5">{t.status === 'completed' ? <Check size={13} className="text-ok" /> : t.status === 'in_progress' ? <Loader2 size={13} className="animate-spin text-accent" /> : <Square size={12} className="text-fg-faint" />}</span>
            {t.status === 'in_progress' && t.activeForm ? t.activeForm : t.content}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A question already answered (or still open): what was asked and what came back. */
/**
 * Claude's answer note, `User has answered your questions: "Q"="A", "Q2"="A2". …`,
 * as question → answer. Quotes inside are backslash-escaped.
 */
export function answersOf(text: string): Map<string, string> {
  const out = new Map<string, string>()
  const un = (v: string) => v.replace(/\\(.)/g, '$1')
  for (const m of text.matchAll(/"((?:[^"\\]|\\.)*)"="((?:[^"\\]|\\.)*)"/g)) out.set(un(m[1]!), un(m[2]!))
  return out
}

/** A question Claude put to the person: each question with its options, the chosen ones ticked. */
function AskedQuestion({ input, result }: { input: Record<string, unknown>; result: ChatItem | undefined }) {
  const qs = Array.isArray(input.questions) ? (input.questions as ChatQuestion[]) : []
  const answers = result?.text && !result.is_error ? answersOf(result.text) : new Map<string, string>()
  const parsed = answers.size > 0
  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-surface-2 px-3 py-2.5 text-[13px]">
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><MessageCircleQuestion size={14} />{!result ? 'Claude is asking…' : result.is_error ? 'Claude asked; not answered' : 'Claude asked'}</div>
      {qs.map((q, i) => {
        const a = answers.get(q.question)
        // Several picks come back joined with ", "; a typed answer matches no option.
        const picked = new Set(a ? (q.multiSelect ? a.split(/,\s*/) : [a]) : [])
        const own = a && !(q.options ?? []).some((o) => picked.has(o.label)) ? a : null
        return (
          <div key={i} className="flex flex-col gap-1.5">
            <div className="flex items-baseline gap-2">
              {q.header && <span className="shrink-0 rounded bg-surface-3 px-1.5 py-px text-[10px] text-fg-subtle">{q.header}</span>}
              <span className="font-medium">{q.question}</span>
            </div>
            {!!q.options?.length && (
              <div className="flex flex-wrap gap-1.5">
                {q.options.map((o) => {
                  const on = picked.has(o.label)
                  return (
                    <span key={o.label} title={o.description}
                      className={cn('flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs', on ? 'border-ok/50 bg-ok/10 text-foreground' : 'text-muted-foreground', parsed && !on && 'opacity-60')}>
                      {on && <Check size={12} className="text-ok" />}{o.label}
                    </span>
                  )
                })}
              </div>
            )}
            {own && <div className="text-xs"><span className="text-muted-foreground">Answer: </span>{own}</div>}
          </div>
        )
      })}
      {/* Not in the usual form (or an error): the note as Claude got it. */}
      {result?.text && !parsed && <div className={cn('whitespace-pre-wrap text-xs', result.is_error ? 'text-err' : 'text-muted-foreground')}>{result.text}</div>}
    </div>
  )
}
